import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Mail, MessageSquare, Plug } from "lucide-react";
import { AdminPageHeader, AdminSectionCard } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  fetchAccountChannels,
  type AccountChannel,
} from "@/lib/account-channels";
import { cn } from "@/lib/utils";

function statusLabel(channel: AccountChannel) {
  if (channel.coming_soon) return "即将上线";
  if (channel.enabled) return "已开启";
  return "未开启";
}

function ChannelIcon({ id }: { id: string }) {
  if (id === "sms") return <MessageSquare className="size-5" />;
  if (id === "email") return <Mail className="size-5" />;
  return <Plug className="size-5" />;
}

/** 账户渠道总览：企微 / 短信 / 邮件 */
export default function AccountChannelsPage() {
  const { accountId } = useAccountWorkspace();
  const [channels, setChannels] = useState<AccountChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAccountChannels(accountId);
      setChannels(res.channels ?? []);
    } catch {
      setError("无法加载渠道列表");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="渠道"
        description="管理账户触达渠道。旅程与 AI 只能选用已开启的渠道。"
      />

      {loading ? <p className="text-sm text-muted-foreground">加载中…</p> : null}
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        {channels.map((channel) => {
          const isWecom = channel.id === "wecom";
          return (
            <AdminSectionCard
              key={channel.id}
              title={channel.label}
              description={
                isWecom
                  ? "企业微信接入与客户联系"
                  : channel.coming_soon
                    ? "即将上线，暂不可选用"
                    : channel.label
              }
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ChannelIcon id={channel.id} />
                  <span
                    className={cn(
                      "rounded-md px-2 py-0.5 text-xs font-medium",
                      channel.enabled
                        ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300"
                        : channel.coming_soon
                          ? "bg-muted text-muted-foreground"
                          : "bg-amber-500/15 text-amber-800 dark:text-amber-300",
                    )}
                  >
                    {statusLabel(channel)}
                  </span>
                </div>
                {isWecom ? (
                  <Button asChild size="sm" variant={channel.enabled ? "outline" : "default"}>
                    <Link to={`/accounts/${accountId}/wecom`}>
                      {channel.enabled ? "管理接入" : "去接入"}
                    </Link>
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" disabled>
                    即将上线
                  </Button>
                )}
              </div>
            </AdminSectionCard>
          );
        })}
      </div>
    </div>
  );
}
